import { useTranslation } from "react-i18next";
import SegmentedControl from "../../../../ui/navigation/SegmentedControl";
import { DETAIL_LEVELS, type DetailLevel } from "../../../knowledgeData";
import type { RegestSummary, TextFacts } from "./regestFacts";

interface RegestHeaderProps {
  /** The counts; `null` while there is nothing to count. */
  readonly summary: RegestSummary | null;
  readonly level: DetailLevel;
  readonly onLevel: (level: DetailLevel) => void;
}

/**
 * The regest's header, as Daniel's viewer heads a vita (render_vita): how many texts and statements, the years its
 * texts are dated, how far it is reviewed, and his switch between three levels of detail. Counts only; no other
 * words (IM-RULES).
 */
export default function RegestHeader({ summary, level, onLevel }: RegestHeaderProps) {
  const { t } = useTranslation();
  const [first, last] = summary?.years ?? [null, null];
  const facts = summary === null ? [] : [
    t("sources.document.regest.textCount", { count: summary.texts }),
    first === null ? null : t("sources.document.regest.dated", { years: first === last ? String(first) : `${String(first)}–${String(last)}` }),
    t("sources.document.added.statementCount", { count: summary.statements }),
    summary.accepted === 0 ? null : t("sources.document.regest.acceptedCount", { count: summary.accepted }),
    summary.proposed === 0 ? null : t("sources.document.regest.proposedCount", { count: summary.proposed }),
  ].filter((fact) => fact !== null);
  return (
    <div className="flex flex-wrap items-center justify-between gap-2 pb-1">
      <p className="app-text-muted text-xs">{facts.join(" · ")}</p>
      <SegmentedControl<DetailLevel>
        label={t("data.abox.detailLevel")}
        options={DETAIL_LEVELS.map((key) => ({ key, label: t(`data.abox.levels.${key}`) }))}
        selected={level}
        onSelect={onLevel}
      />
    </div>
  );
}

/** A text's date and archive reference in its head (Daniel's render_source): the date, its calendar, the reference. */
export function TextHead({ facts, nameOf }: { facts: TextFacts; nameOf: (iri: string) => string }) {
  const { date, archive } = facts;
  return (
    <span className="app-text-muted">
      {date === null ? null : <span title={date.original ?? undefined}>{date.text}{date.calendar === null ? null : ` (${date.calendar.kind === "term" ? nameOf(date.calendar.iri) : date.calendar.kind === "individual" ? (date.calendar.individual.name ?? nameOf(date.calendar.individual.iri)) : date.calendar.text})`}</span>}
      {date === null || archive === null ? null : " · "}
      {archive === null ? null : <span>{nameOf(archive.property)}: {archive.text}</span>}
    </span>
  );
}
